import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  AlertTriangle,
  Unlock,
  Lock,
  Building2,
} from 'lucide-react';

import OnboardingWizard from '../components/OnboardingWizard';
import RoleDashboard from '../components/dashboard/RoleDashboard';
import PageHeader from '../components/layout/PageHeader';
import { resolveMagasinRole, shouldShowCaisseInHeader } from '../utils/roleRouting';
import { DASH_PANEL, DASH_BTN_PRIMARY, DASH_BTN_SECONDARY } from '../features/dashboard/dashboardV2Ui';
import { LayoutDashboard } from 'lucide-react';

const DENOMINATIONS = [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];
const CAISSE_MAGASINS = ['M1', 'M2'];
const buildDenoms = () => DENOMINATIONS.map(value => ({ value, quantity: 0 }));

/* ── Caisse bandeau inline (Option A — LOT-UI-01) ── */
const CaisseStatusBar = ({ stats, currentHour, onOuvrir, onForceOpen, canManageCaisse, t }) => {
  if (stats?.caisse_ouverte) {
    return (
      <div className={`${DASH_PANEL} p-4 flex items-center justify-between flex-wrap gap-4 border-emerald-500/20 bg-emerald-950/10`} data-testid="caisse-status-bar">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="font-semibold text-emerald-300">{t('dash.caisseOpen')}</span>
          {currentHour >= 19 && (
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              {t('dash.closeCaisseReminder')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManageCaisse && (
            <button type="button" onClick={onForceOpen} className={DASH_BTN_SECONDARY} data-testid="force-open-caisse-btn">
              <Unlock className="w-4 h-4 inline mr-1" /> {t('dash.forceOpen')}
            </button>
          )}
          {canManageCaisse && (
            <button type="button" onClick={() => { window.location.href = '/fermer-caisse'; }} className={DASH_BTN_PRIMARY} data-testid="fermer-caisse-btn">
              <Lock className="w-4 h-4 inline mr-1" /> {t('dash.closeCaisse')}
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={`${DASH_PANEL} p-4 flex items-center justify-between flex-wrap gap-4 border-red-500/20 bg-red-950/10`} data-testid="caisse-status-bar">
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="font-semibold text-white">{t('dash.noCaisseToday') || t('dash.noCaisse')}</span>
        </div>
        <p className="text-xs text-gray-500">{t('dash.salesBlocked')}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {canManageCaisse && (
          <button type="button" onClick={onForceOpen} className={DASH_BTN_SECONDARY} data-testid="force-open-caisse-btn">
            <Unlock className="w-4 h-4 inline mr-1" /> {t('dash.force')}
          </button>
        )}
        {canManageCaisse && (
          <button type="button" onClick={onOuvrir} className={`${DASH_BTN_PRIMARY} bg-emerald-600 hover:bg-emerald-500`} data-testid="ouvrir-caisse-btn">
            <Unlock className="w-4 h-4 inline mr-1" /> {t('dash.openCaisse')}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Caisse Open Modal (shared) ── */
const CaisseOpenModal = ({ openDenoms, updateDenoms, setOpenDenoms, totalOpen, onClose, onConfirm, loading, t, formatCurrency, forceMode, magasinId }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
    <div className="modal-dark w-full max-w-md">
      <div className="modal-dark-header flex items-center justify-between">
        <h3 className="font-bold text-lg">{forceMode ? t('dash.forceOpenTitle', { magasin: magasinId }) : `${t('dash.openCaisseTitle')} ${magasinId}`}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="open-caisse-modal-close">✕</button>
      </div>
      <div className="p-6">
        {forceMode && (
          <div className="mb-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs">
            {t('dash.forceOpenWarning')}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {openDenoms.map((item, index) => (
            <div key={item.value} className="flex items-center justify-between gap-2">
              <span className="text-sm text-gray-300">{formatCurrency(item.value)}</span>
              <input type="number" min="0" value={item.quantity} onChange={(e) => updateDenoms(setOpenDenoms, index, e.target.value)} className="input-dark w-20 py-2" data-testid={`open-denom-${item.value}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-sm text-gray-300 mb-6">
          <span>{t('dash.openTotal')}</span>
          <span className="text-emerald-300 font-bold">{formatCurrency(totalOpen)}</span>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold transition-colors" data-testid="open-caisse-cancel">{t('common.cancel')}</button>
          <button type="button" onClick={onConfirm} disabled={loading} className={`flex-1 py-3 rounded-xl ${DASH_BTN_PRIMARY}`} data-testid="confirm-ouvrir-caisse">{loading ? t('dash.opening') : t('dash.open')}</button>
        </div>
      </div>
    </div>
  </div>
);

// Normalise un magasin_id de façon défensive : un id non-string (nombre,
// tableau, valeur inattendue venant de la base) ne doit jamais crasher le
// rendu via .toUpperCase(). Fallback 'M1'.
const normMagasinId = (m) => {
  if (typeof m === 'string' && m.trim()) return m.trim().toUpperCase();
  if (Array.isArray(m) && typeof m[0] === 'string' && m[0].trim()) return m[0].trim().toUpperCase();
  return 'M1';
};

const DashboardPage = () => {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const canManageCaisse = isAdmin || ['user', 'commercial', 'technicien'].includes(user?.role);
  const { t, tError } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caisseLoading, setCaisseLoading] = useState(false);
  const [openDenoms, setOpenDenoms] = useState(buildDenoms());
  const [showCaisseModal, setShowCaisseModal] = useState(false);
  const [unclosedCaisse, setUnclosedCaisse] = useState(null);
  const [forceOpenMode, setForceOpenMode] = useState(false);
  const [selectedCaisseMagasin, setSelectedCaisseMagasin] = useState(normMagasinId(user?.default_magasin_id));
  const [caisseStatusByMagasin, setCaisseStatusByMagasin] = useState({
    M1: { is_open: false, magasin_id: 'M1' },
    M2: { is_open: false, magasin_id: 'M2' },
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);

  useEffect(() => { fetchStats(); checkUnclosedCaisse(); checkOnboarding(); fetchOpenTickets(); fetchCaisseStatuses(); }, []);

  useEffect(() => {
    // Poll tickets every 60s
    const iv = setInterval(fetchOpenTickets, 60000);
    return () => clearInterval(iv);
  }, []);


  const fetchUnclosedCaisseForMagasin = async (magasinId) => {
    try {
      const res = await api.get('/caisse/check-unclosed', { params: { magasin_id: magasinId } });
      return res.data?.has_unclosed ? res.data : null;
    } catch {
      return null;
    }
  };

  const resolveForceCloseTarget = async (magasinId) => {
    const mag = normMagasinId(magasinId);
    const unclosed = await fetchUnclosedCaisseForMagasin(mag);
    if (unclosed?.caisse_id) {
      return {
        caisse_id: unclosed.caisse_id,
        date_caisse: unclosed.date_caisse,
        magasin_id: unclosed.magasin_id || mag,
      };
    }
    try {
      const res = await api.get('/caisse/status', { params: { magasin_id: mag } });
      const data = res.data;
      if (data?.id && data?.is_open !== false) {
        return {
          caisse_id: data.id,
          date_caisse: data.date_caisse,
          magasin_id: data.magasin_id || mag,
        };
      }
    } catch {
      // ignore — no open caisse for this magasin
    }
    return null;
  };

  const checkUnclosedCaisse = async () => {
    try {
      const role = resolveMagasinRole(user);
      const magasinsToScan = (role === 'super_admin' || role === 'admin_magasin')
        ? CAISSE_MAGASINS
        : [normMagasinId(user?.default_magasin_id)];
      const hits = await Promise.all(magasinsToScan.map((mag) => fetchUnclosedCaisseForMagasin(mag)));
      let oldest = null;
      for (const hit of hits) {
        if (hit && (!oldest || hit.date_caisse < oldest.date_caisse)) {
          oldest = hit;
        }
      }
      setUnclosedCaisse(oldest);
    } catch {
      setUnclosedCaisse(null);
    }
  };

  const fetchCaisseStatuses = async () => {
    try {
      const results = await Promise.all(
        ['M1', 'M2'].map(async (magasinId) => {
          try {
            const res = await api.get('/caisse/status', { params: { magasin_id: magasinId } });
            return [magasinId, { ...res.data, magasin_id: magasinId }];
          } catch {
            return [magasinId, { is_open: false, magasin_id: magasinId }];
          }
        })
      );
      setCaisseStatusByMagasin(Object.fromEntries(results));
    } catch {}
  };

  const checkOnboarding = async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/onboarding/status');
      if (!res.data.onboarding_completed) setShowOnboarding(true);
    } catch {}
  };

  const handleForceCloseCaisse = async (targetMagasin = null) => {
    let target = null;
    if (targetMagasin) {
      target = await resolveForceCloseTarget(targetMagasin);
    } else if (unclosedCaisse?.caisse_id) {
      target = unclosedCaisse;
    } else {
      target = await resolveForceCloseTarget(selectedCaisseMagasin);
    }
    if (!target?.caisse_id) {
      alert(t('dash.noOpenCaisseToForce', { magasin: targetMagasin || t('dash.thisMagasin') }));
      return;
    }
    if (!window.confirm(`${t('dash.forceCloseConfirm')} ${target.date_caisse} (${target.magasin_id || targetMagasin || 'M1'}) ?`)) return;
    setCaisseLoading(true);
    try {
      await api.post(`/caisse/force-close/${target.caisse_id}`);
      if (!targetMagasin || (unclosedCaisse?.caisse_id === target.caisse_id)) {
        setUnclosedCaisse(null);
      }
      await checkUnclosedCaisse();
      fetchStats();
      fetchCaisseStatuses();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('dash.errorForceClose'));
    } finally { setCaisseLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenTickets = async () => {
    try {
      const res = await api.get('/chat/support-tickets');
      const open = (res.data || []).filter(t => t.status === 'open');
      setOpenTicketsCount(open.length);
    } catch {}
  };

  const totalOpen = useMemo(() =>
    openDenoms.reduce((sum, item) => sum + item.value * item.quantity, 0),
  [openDenoms]);

  const updateDenoms = (setter, index, value) => {
    const qty = Math.max(0, parseInt(value, 10) || 0);
    setter(prev => prev.map((item, i) => (i === index ? { ...item, quantity: qty } : item)));
  };

  const openCaisseForMagasin = (magasinId, force = false) => {
    const targetMagasin = normMagasinId(magasinId);
    setSelectedCaisseMagasin(targetMagasin);
    setForceOpenMode(force);
    navigate(`/ouvrir-caisse?magasin_id=${targetMagasin}${force ? '&force=1' : ''}`);
  };

  const handleOuvrirCaisse = async () => {
    setCaisseLoading(true);
    try {
      const endpoint = forceOpenMode ? '/caisse/force-open' : '/caisse/ouvrir';
      await api.post(endpoint, { total: totalOpen, details: openDenoms, magasin_id: selectedCaisseMagasin });
      setShowCaisseModal(false);
      setForceOpenMode(false);
      setUnclosedCaisse(null);
      fetchStats();
      fetchCaisseStatuses();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('dash.errorOpenCaisse'));
    } finally {
      setCaisseLoading(false);
    }
  };

  const renderExplicitCaisseActions = (mag) => {
    const isOpen = !!caisseStatusByMagasin?.[mag]?.is_open;

    return (
      <div key={mag} className={`${DASH_PANEL} p-4 space-y-3`}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-bold text-white">{mag}</div>
          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${isOpen ? 'bg-emerald-950/40 text-emerald-300 border-emerald-500/30' : 'bg-amber-950/40 text-amber-300 border-amber-500/30'}`}>
            {isOpen ? t('dash.statusOpenF') : t('dash.statusClosedF')}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {!isOpen ? (
            <>
              <button type="button" onClick={() => openCaisseForMagasin(mag, false)} className={DASH_BTN_PRIMARY}>
                {t('dash.openMagasin', { magasin: mag })}
              </button>
              <button type="button" onClick={() => openCaisseForMagasin(mag, true)} className={DASH_BTN_SECONDARY}>
                {t('dash.forceOpenShort')}
              </button>
            </>
          ) : (
            <>
              <a href={`/fermer-caisse?magasin_id=${mag}`} className={`${DASH_BTN_PRIMARY} text-center`}>
                {t('dash.closeMagasin', { magasin: mag })}
              </a>
              <button type="button" onClick={() => handleForceCloseCaisse(mag)} className={DASH_BTN_SECONDARY}>
                {t('dash.forceCloseShort')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const currentHour = new Date().getHours();
  const magasinRole = resolveMagasinRole(user);
  const showCaisse = shouldShowCaisseInHeader(user);
  const showExplicitCaisse = magasinRole === 'super_admin' || magasinRole === 'admin_magasin';

  if (showOnboarding) {
    return <OnboardingWizard onComplete={() => setShowOnboarding(false)} />;
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] bg-[#0f1117] flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  const dashboardSubtitle = magasinRole === 'super_admin'
    ? t('dash.subtitleNetwork')
    : magasinRole === 'technicien'
      ? t('dash.subtitleWorkshop')
      : t('dash.subtitleDefault');

  return (
    <Layout>
      {unclosedCaisse && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]" data-testid="unclosed-caisse-blocker">
          <div className={`${DASH_PANEL} p-6 max-w-md w-full border-red-500/30`}>
            <div className="text-center space-y-4">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
              <h3 className="text-xl font-bold text-red-300">{t('dash.unclosedTitle')}</h3>
              <p className="text-gray-300 text-sm">{t('dash.title')} <span className="font-bold text-white">{unclosedCaisse.date_caisse}</span> {t('dash.unclosedNotClosed')}.</p>
              <p className="text-gray-400 text-xs">{t('dash.mustClose')}</p>
              <div className="flex flex-col gap-3 pt-2">
                <button type="button" onClick={handleForceCloseCaisse} disabled={caisseLoading}
                  className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="force-close-caisse-btn">
                  <Lock className="w-4 h-4" />
                  {caisseLoading ? t('dash.closing') : t('dash.forceClose')}
                </button>
                <a href={`/fermer-caisse?magasin_id=${unclosedCaisse?.magasin_id || 'M1'}`} className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-center">
                  {t('dash.closeNormal')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[#0f1117] text-white pb-24 lg:pb-8" data-testid="dashboard-page-root">
      <div className="max-w-7xl mx-auto fade-in space-y-5 px-1 sm:px-0 py-1" data-testid="dashboard-page">
        <PageHeader
          title={t('dash.title')}
          subtitle={dashboardSubtitle}
          icon={LayoutDashboard}
          testId="dashboard-page-header"
        />

        {showCaisse && (
          <CaisseStatusBar stats={stats} currentHour={currentHour} onOuvrir={() => openCaisseForMagasin(selectedCaisseMagasin, false)} onForceOpen={() => openCaisseForMagasin(selectedCaisseMagasin, true)} canManageCaisse={canManageCaisse} t={t} />
        )}

        {showExplicitCaisse && (
          <div className={`${DASH_PANEL} p-4 space-y-4`} data-testid="explicit-caisse-actions">
            <div className="flex items-center gap-2 text-white font-bold text-sm">
              <Building2 className="w-5 h-5 text-red-400" />
              {t('dash.caisseActionsByStore')}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['M1', 'M2'].map(renderExplicitCaisseActions)}
            </div>
          </div>
        )}

        <RoleDashboard
          user={user}
          stats={stats}
          caisseStatusByMagasin={caisseStatusByMagasin}
          openTicketsCount={openTicketsCount}
          t={t}
        />
      </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
